import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import refreshIcon from '../../../assets/images/indices-refresh.svg';
import closeIcon from '../../../assets/images/indices-close.svg';
import {
  INDICES_PERIODS,
  type IndexInstrumentViewModel,
  type IndicesPeriod,
} from '@app-types/indices';
import { buildIndicesChartBundlePayload } from '@utils/indicesChartBundle';
import IndicesChartFrame from './IndicesChartFrame';

interface IndicesChartDialogProps {
  instrument: IndexInstrumentViewModel;
  period: IndicesPeriod;
  refreshRevision: number;
  returnFocusRef: React.RefObject<HTMLElement | null>;
  onSelectPeriod: (period: IndicesPeriod) => void;
  onRefresh: () => void | Promise<void>;
  isRefreshing: boolean;
  canRefresh: boolean;
  onClose: () => void;
}

const PERIODS: ReadonlyArray<{ value: IndicesPeriod; label: string }> = [
  { value: INDICES_PERIODS.daily, label: '日足' },
  { value: INDICES_PERIODS.weekly, label: '週足' },
  { value: INDICES_PERIODS.monthly, label: '月足' },
];

const IndicesChartDialog: React.FC<IndicesChartDialogProps> = ({
  instrument,
  period,
  refreshRevision,
  returnFocusRef,
  onSelectPeriod,
  onRefresh,
  isRefreshing,
  canRefresh,
  onClose,
}) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButtonRef.current?.focus();
    return () => returnFocusRef.current?.focus();
  }, [returnFocusRef]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  };

  return createPortal(
    <div
      className="indices-chart-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="indices-chart-dialog-title"
      onKeyDown={handleKeyDown}
      data-testid="indices-chart-dialog"
    >
      <div className="indices-chart-dialog__click-catcher" aria-hidden="true" />
      <div className="indices-chart-dialog__panel">
        <header className="indices-chart-dialog__header">
          <h2 className="indices-chart-dialog__title" id="indices-chart-dialog-title">
            {instrument.name}
          </h2>
          <div className="indices-chart-dialog__actions">
            <button
              className="indices-chart-dialog__icon-button"
              type="button"
              aria-label="チャートを更新"
              aria-busy={isRefreshing}
              disabled={isRefreshing || !canRefresh}
              onClick={onRefresh}
            >
              <img src={refreshIcon} alt="" aria-hidden="true" />
            </button>
            <button
              ref={closeButtonRef}
              className="indices-chart-dialog__icon-button"
              type="button"
              aria-label="チャートを閉じる"
              onClick={onClose}
            >
              <img src={closeIcon} alt="" aria-hidden="true" />
            </button>
          </div>
        </header>
        <div className="indices-chart-dialog__body">
          <div className="indices-chart-dialog__quote">
            <span className="indices-chart-dialog__value">{instrument.valueText}</span>
            <span className={`indices-chart-dialog__change indices-chart-dialog__change--${instrument.changeTone}`}>
              {instrument.changeText}
            </span>
            <span className="indices-chart-dialog__updated-at">{instrument.updatedAtText}</span>
          </div>
          <div className="indices-chart-dialog__periods" role="group" aria-label="表示期間">
            {PERIODS.map((option) => {
              const isActive = period === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  className={'indices-chart-dialog__period'
                    + (isActive ? ' indices-chart-dialog__period--active' : '')}
                  aria-pressed={isActive}
                  onClick={() => onSelectPeriod(option.value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <div className="indices-chart-dialog__chart" data-chart-state={instrument.chartState}>
            {instrument.chartState === 'ready' && instrument.chartData ? (
              <IndicesChartFrame
                payload={buildIndicesChartBundlePayload({
                  ric: instrument.ric,
                  chartData: instrument.chartData,
                  decimalPlaces: instrument.decimalPlaces,
                }, period, 'popup')}
                title={`${instrument.name} 詳細チャート`}
                refreshRevision={refreshRevision}
              />
            ) : (
              <div className="indices-chart-dialog__chart-state" role="status">
                {instrument.chartState === 'loading' ? '読み込み中…' : 'データなし'}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default IndicesChartDialog;
